
import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Container, Modal, ModalHeader, ModalBody, ModalFooter, Label, Input, FormGroup, Row } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../CommonElements/Breadcrumbs";
import { apiGet, apiPost } from "../../Utils";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Btn, H3, H4, H5, ProgressBar } from "../../AbstractElements";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useNavigate } from "react-router-dom";
import { Edit, Plus, Trash2, Repeat } from 'react-feather';
import Swal from 'sweetalert2';
import { useAuth } from "../../Context/AuthContext";

interface VehicleData {
    id: number;
    branch_id: number;
    status: string;
    category: string;
    brand: string;
    model: string;
    manufacture_year: number;
    color: string;
    registration_number: string;
    registration_expiry_date: string; // YYYY-MM-DD
    vin_chassis_number: string;
    assigned_driver_id?: number;
    purchase_date: string;
    purchase_cost: number;
    vehicle_image?: string;
    rc_document?: string;
    next_service_due: string;
    owner_name?: string;
    owner_mobile?: string;
    owner_email?: string;
    fuel_type?: string;
    insurance_provider?: string;
    insurance_policy_number?: string;
    insurance_expiry_date?: string;
    branch?: { branch_name: string };
    driver?: { name: string };
}

export default function ViewVehicle() {
    const [data, setData] = useState<VehicleData[]>([]);
    const [loading, setLoading] = useState(false);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [refresh, setRefresh] = useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortColumn, setSortColumn] = useState("");
    const [sortDirection, setSortDirection] = useState("");
    const prevSearch = React.useRef(searchTerm);
    const navigate = useNavigate();
    const { hasPermission } = useAuth();

    // Transfer Modal State
    const [branches, setBranches] = useState<any[]>([]);
    const [drivers, setDrivers] = useState<any[]>([]);
    const [modal, setModal] = useState(false);
    const [transferData, setTransferData] = useState({
        vehicleId: 0,
        vehicleReg: '',
        currentBranch: '',
        currentDriver: '',
        newBranchId: '',
        newDriverId: '',
        driver_assigned_at: '',
        odometer_reading: ''
    });
    const [transferLoading, setTransferLoading] = useState(false);

    useEffect(() => {
        const fetchOptions = async () => {
            try {
                const branchData = await apiPost("branches/list", { status: 'Active' });
                setBranches(branchData);
            } catch (error) {
                console.error("Error fetching options:", error);
            }
        };
        fetchOptions();
    }, []);

    const fetchDriversForBranch = async (branchId: string) => {
        try {
            const response: any = await apiPost("drivers/list", { branch_id: branchId });
            // If the API returns paginated data (wrapper), handle it.
            // Our get_all returns JSON array if no per_page, or object if per_page.
            // We are not passing per_page, so it returns all (array).
            if (Array.isArray(response)) {
                setDrivers(response);
            } else if (response.data) {
                setDrivers(response.data);
            } else {
                setDrivers([]);
            }
        } catch (error) {
            console.error("Error fetching drivers:", error);
            setDrivers([]);
        }
    };

    useEffect(() => {
        const isSearchChanged = prevSearch.current !== searchTerm;
        const delay = isSearchChanged ? 500 : 0;
        prevSearch.current = searchTerm;

        const timer = setTimeout(() => {
            fetchData(currentPage, perPage, searchTerm, sortColumn, sortDirection);
        }, delay);
        return () => clearTimeout(timer);
    }, [refresh, currentPage, perPage, searchTerm, sortColumn, sortDirection]);

    const fetchData = async (page: number, size: number, search: string = "", sortCol: string = "", sortDir: string = "") => {
        setLoading(true);
        try {
            const body: any = {
                page: page,
                per_page: size,
                search: search
            };

            if (sortCol) {
                body.sort_by = sortCol;
                body.sort_direction = sortDir;
            }

            const response: any = await apiPost("vehicles/getall", body);
            if (response && response.data) {
                setData(response.data);
                setTotalRows(response.total || 0);
            } else {
                setData(response);
                setTotalRows(response.length || 0);
            }
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (column: any, sortDir: string) => {
        if (column.sortField) {
            setSortColumn(column.sortField);
            setSortDirection(sortDir);
            setCurrentPage(1);
        }
    };

    const handlePageChange = (page: number) => {
        setLoading(true);
        setCurrentPage(page);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setLoading(true);
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const handleAdd = () => {
        navigate(`${process.env.PUBLIC_URL}/add_vehicle`);
    };

    const handleEdit = (id: number) => {
        navigate(`${process.env.PUBLIC_URL}/edit_vehicle`, { state: { id: id } });
    };

    const handleDelete = async (id: number) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await apiPost("vehicles/delete", { id: id });
                    toast.success("Vehicle deleted successfully", { autoClose: 1000 });
                    setRefresh(prev => prev + 1);
                } catch (error) {
                    toast.error("Failed to delete vehicle", { autoClose: 1000 });
                }
            }
        });
    };

    const openTransferModal = (row: VehicleData) => {
        setTransferData({
            vehicleId: row.id,
            vehicleReg: row.registration_number,
            currentBranch: row.branch?.branch_name || 'N/A',
            currentDriver: row.driver?.name || 'Unassigned',
            newBranchId: '',
            newDriverId: '',
            driver_assigned_at: new Date().toISOString(),
            odometer_reading: ''
        });
        setDrivers([]);
        setModal(true);
    };

    const handleTransferSubmit = async () => {
        if (!transferData.newBranchId) {
            toast.error("Please select a branch");
            return;
        }
        if (!transferData.newDriverId) {
            toast.error("Please select a driver");
            return;
        }

        setTransferLoading(true);
        try {
            await apiPost("vehicles/transfer", {
                vehicle_id: transferData.vehicleId,
                branch_id: transferData.newBranchId,
                driver_id: transferData.newDriverId,
                odometer_reading: transferData.odometer_reading,
                driver_assigned_at: transferData.driver_assigned_at
            });
            toast.success("Vehicle transferred successfully");
            setModal(false);
            setRefresh(prev => prev + 1);
        } catch (error: any) {
            toast.error(error.response?.data?.message || "Transfer failed");
        } finally {
            setTransferLoading(false);
        }
    };



    const calculateProfileCompletion = (row: VehicleData) => {
        const mandatoryFields = [
            'branch_id', 'status', 'category', 'brand', 'model',
            'manufacture_year', 'color', 'registration_number', 'registration_expiry_date',
            'vin_chassis_number', 'assigned_driver_id', 'purchase_date', 'purchase_cost',
            'next_service_due', 'insurance_provider', 'insurance_policy_number', 'insurance_expiry_date',
            'owner_name', 'owner_mobile', 'owner_email', 'fuel_type',
            'vehicle_image', 'rc_document'
        ];
        let filledCount = 0;
        mandatoryFields.forEach(field => {
            const value = row[field as keyof VehicleData];
            if (value !== null && value !== undefined && value !== "") {
                filledCount++;
            }
        });
        return Math.min(Math.round((filledCount / mandatoryFields.length) * 100), 100);
    };

    const filteredDrivers = drivers.filter(d => d.assigned_branch_id?.toString() === transferData.newBranchId?.toString());

    const columns = [
        {
            name: "Sl. No",
            selector: (row: VehicleData, index?: number) => (currentPage - 1) * perPage + (index || 0) + 1,
            width: "80px",
        },
        {
            name: "Model",
            selector: (row: VehicleData) => `${row.brand} ${row.model}`,
            sortable: true,
            sortField: "model",
            wrap: true,
        },
        {
            name: "Reg No",
            selector: (row: VehicleData) => row.registration_number,
            sortable: true,
            sortField: "registration_number",
            wrap: true,
        },

        {
            name: "Driver",
            selector: (row: VehicleData) => row.driver?.name || "Unassigned",
            sortable: true,
            sortField: "driver_name",
            wrap: true,
        },
        {
            name: "Branch",
            selector: (row: VehicleData) => row.branch?.branch_name || "Unassigned",
            sortable: true,
            sortField: "branch_name",
            wrap: true,
        },
        {
            name: "Next Service",
            selector: (row: VehicleData) => row.next_service_due,
            sortable: true,
            sortField: "next_service_due",
            cell: (row: VehicleData) => row.next_service_due ? new Date(row.next_service_due).toLocaleDateString('en-GB') : "N/A"
        },
        {
            name: "Profile Status",
            cell: (row: VehicleData) => {
                const completion = calculateProfileCompletion(row);
                return (
                    <div className="w-100">
                        <div className="progress-bar-animated progress-bar-striped">
                            <ProgressBar
                                value={completion}
                                color={completion === 100 ? 'success' : 'warning'}
                                striped={completion <= 100}
                                animated={completion < 100}
                                style={{ height: '8px' }}
                            />
                        </div>
                        <div className="text-center small mt-1">{completion}%</div>
                    </div>
                );
            },
            sortable: false,
            width: "150px"
        },
        {
            name: "Status",
            cell: (row: VehicleData) => (
                <span className={`badge ${row.status === 'Active' ? 'bg-success' : row.status === 'Maintenance' ? 'bg-warning' : 'bg-secondary'}`}>
                    {row.status}
                </span>
            ),
            sortable: true,
            sortField: "status",
        },
        {
            name: "Year",
            selector: (row: VehicleData) => row.manufacture_year,
            sortable: true,
            omit: true
        },
        {
            name: "Color",
            selector: (row: VehicleData) => row.color,
            sortable: true,
            omit: true
        },
        {
            name: "Fuel",
            selector: (row: VehicleData) => row.fuel_type || '',
            sortable: true,
            omit: true
        },
        {
            name: "Reg Expiry",
            selector: (row: VehicleData) => row.registration_expiry_date,
            sortable: true,
            omit: true
        },
        {
            name: "VIN",
            selector: (row: VehicleData) => row.vin_chassis_number,
            sortable: true,
            omit: true
        },
        {
            name: "Purchase Date",
            selector: (row: VehicleData) => row.purchase_date,
            sortable: true,
            omit: true
        },
        {
            name: "Cost",
            selector: (row: VehicleData) => row.purchase_cost,
            sortable: true,
            omit: true
        },

        {
            name: "Insurance",
            selector: (row: VehicleData) => row.insurance_provider || '',
            sortable: true,
            omit: true
        },
        {
            name: "Policy No",
            selector: (row: VehicleData) => row.insurance_policy_number || '',
            sortable: true,
            omit: true
        },
        {
            name: "Ins Expiry",
            selector: (row: VehicleData) => row.insurance_expiry_date || '',
            sortable: true,
            omit: true
        },
        {
            name: "Owner",
            selector: (row: VehicleData) => row.owner_name || '',
            sortable: true,
            omit: true
        },
        {
            name: "Actions",
            cell: (row: VehicleData) => (
                <div className="d-flex justify-content-between gap-2">

                    <Button size="sm" color="info" onClick={() => handleEdit(row.id)} title="Edit">
                        <Edit size={14} />
                    </Button>
                    {hasPermission('vehicle.delete') && (
                        <Button size="sm" color="danger" onClick={() => handleDelete(row.id)} title="Delete">
                            <Trash2 size={14} />
                        </Button>
                    )}
                    {hasPermission('vehicle.transfer') && row.branch_id && row.assigned_driver_id && row.status !== 'Sold' && row.status !== 'Destroyed' && (
                        <Button size="sm" color="warning" onClick={() => openTransferModal(row)} title="Transfer Vehicle">
                            <Repeat size={14} />
                        </Button>
                    )}
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className='page-body'>
            <Breadcrumbs pageTitle="View Vehicle" parent="General" title="View Vehicle" />
            <Container fluid>
                <Col xl={12} className="mb-lg-3">
                    <Card>
                        <CardBody>
                            <Col xs={12} className="mt-2 d-flex justify-content-between align-items-center mb-3">
                                <H3 className="text-primary">Vehicle Overview</H3>
                                {hasPermission('vehicle.add') && (
                                    <Btn color="primary" onClick={handleAdd}>
                                        <Plus size={16} /> Add Vehicle
                                    </Btn>
                                )}
                            </Col>
                            {hasPermission('vehicle.view') && (
                                <>
                                    <Col xs={12} className="mb-3 d-flex justify-content-end">
                                        <Input type="text" placeholder="Search..." value={searchTerm} onChange={handleSearchChange} style={{ maxWidth: "300px" }} />
                                    </Col>

                                    <div className="table-responsive">
                                        <DataTable
                                            data={data}
                                            columns={columns}
                                            pagination
                                            paginationServer
                                            paginationTotalRows={totalRows}
                                            onChangePage={handlePageChange}
                                            onChangeRowsPerPage={handlePerRowsChange}
                                            onSort={handleSort}
                                            sortServer
                                            progressPending={loading}
                                            progressComponent={<div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}><Spinner color="primary" /></div>}
                                            highlightOnHover
                                            customStyles={customStyles}
                                        />
                                    </div>
                                </>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Container>

            <Modal isOpen={modal} toggle={() => setModal(!modal)}>
                <ModalHeader toggle={() => setModal(!modal)}>Transfer Vehicle</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md={6}>
                            <Label>Current Branch</Label>
                            <Input value={transferData.currentBranch} disabled />
                        </Col>
                        <Col md={6}>
                            <Label>Current Driver</Label>
                            <Input value={transferData.currentDriver} disabled />
                        </Col>
                    </Row>
                    <hr />
                    <Row className="mt-3">
                        <Col md={12}>
                            <FormGroup>
                                <Label>Transfer To Branch <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={transferData.newBranchId}
                                    onChange={(e) => {
                                        const newBranchId = e.target.value;
                                        setTransferData({ ...transferData, newBranchId: newBranchId, newDriverId: '' });
                                        if (newBranchId) {
                                            fetchDriversForBranch(newBranchId);
                                        } else {
                                            setDrivers([]);
                                        }
                                    }}
                                    autoComplete="none"
                                >
                                    <option value="">Select Branch</option>
                                    {branches.map((b: any) => (
                                        <option key={b.id} value={b.id}>{b.branch_name}</option>
                                    ))}
                                </Input>
                            </FormGroup>
                        </Col>
                        <Col md={12}>
                            <FormGroup>
                                <Label>Assign Driver <span className="text-danger">*</span></Label>
                                <Input
                                    type="select"
                                    value={transferData.newDriverId}
                                    onChange={(e) => setTransferData({ ...transferData, newDriverId: e.target.value })}
                                    autoComplete="none"
                                >
                                    <option value="">Select Driver</option>
                                    {drivers.map((d: any) => (
                                        <option key={d.id} value={d.id}>{d.name}</option>
                                    ))}
                                </Input>
                            </FormGroup>

                        </Col>
                        {transferData.newBranchId && transferData.newDriverId && (
                            <>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Transfer Date & Time <span className="text-danger">*</span></Label>
                                        <DatePicker
                                            className="form-control"
                                            selected={transferData.driver_assigned_at ? new Date(transferData.driver_assigned_at) : null}
                                            onChange={(date) => setTransferData({ ...transferData, driver_assigned_at: date ? date.toISOString() : '' })}
                                            showTimeSelect
                                            dateFormat="dd/MM/yyyy HH:mm"
                                            placeholderText="Select Date & Time"
                                            autoComplete="off"
                                            showMonthDropdown
                                            showYearDropdown
                                            dropdownMode="select"
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md={6}>
                                    <FormGroup>
                                        <Label>Odometer Reading <span className="text-danger">*</span></Label>
                                        <Input
                                            type="number"
                                            placeholder="Enter Odometer Reading"
                                            value={transferData.odometer_reading}
                                            onChange={(e) => setTransferData({ ...transferData, odometer_reading: e.target.value })}
                                        />
                                    </FormGroup>
                                </Col>
                            </>
                        )}
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Button color="secondary" onClick={() => setModal(false)}>Cancel</Button>
                    <Button color="primary" disabled={transferLoading} onClick={handleTransferSubmit}>
                        {transferLoading ? <> Transferring <Spinner size="sm" /></> : "Transfer"}
                    </Button>
                </ModalFooter>
            </Modal>
            <ToastContainer autoClose={1000} />
        </div >
    );
}
